import { useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { isAddress } from 'viem'

import { QUERY_KEYS } from '~/constants/react-query'
import {
  ActiveOrgResponse,
  getActiveOrgQueryKey,
  useActiveOrg,
} from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { SmartAccountSetupResponse } from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { isValidEmail } from '~/utils/authUser'
import { emailsMatch } from '~/utils/email'

type ExistingSigner =
  NonNullable<SmartAccountSetupResponse>['eoaSigners'][number]

type SubmitData = { address: string; name: string; email: string }

type Props = {
  existingSigner: ExistingSigner | undefined
  onSuccess: () => void
  onSignerAdded: (newSignerCount: number) => Promise<void>
}

export const useSubmitRecoverySigner = ({
  existingSigner,
  onSuccess,
  onSignerAdded,
}: Props) => {
  const { org } = useActiveOrg()
  const queryClient = useQueryClient()
  const { serverPostRequest } = useServerRequests()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEdit = !!existingSigner
  const wasEmail = !!existingSigner?.email

  const writeSetupToCache = (newSetup: SmartAccountSetupResponse) => {
    if (!org) return
    queryClient.setQueryData<ActiveOrgResponse | undefined>(
      getActiveOrgQueryKey(org.id),
      (prev) =>
        prev
          ? { ...prev, org: { ...prev.org, smartAccountSetup: newSetup } }
          : prev,
    )
  }

  const invalidateContactLookup = async (lookupAddress: string) => {
    if (!org || !isAddress(lookupAddress)) return
    await queryClient.invalidateQueries({
      queryKey: [
        QUERY_KEYS.CONTACT_LOOKUP,
        org.id,
        EvmAddress.format(lookupAddress),
      ],
    })
  }

  const updateNameOnly = async (trimmedName: string) => {
    if (!org || existingSigner?.keySlot === undefined) {
      onSuccess()
      return
    }
    setIsSubmitting(true)
    try {
      const resp = await serverPostRequest<{
        setup: SmartAccountSetupResponse
      }>({
        path: '/api/org/smart_accounts_setup/set_signer_name_by_keyslot',
        params: {
          orgId: org.id,
          keySlot: existingSigner.keySlot,
          name: trimmedName || undefined,
        },
        errorMessage: 'Failed to update recovery signer',
      })
      if (resp) {
        writeSetupToCache(resp.data.setup)
        await invalidateContactLookup(existingSigner.address)
        onSuccess()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitEmail = async (data: SubmitData, trimmedName: string) => {
    if (!org || !isValidEmail(data.email)) return

    if (isEdit && wasEmail && emailsMatch(existingSigner?.email, data.email)) {
      await updateNameOnly(trimmedName)
      return
    }

    setIsSubmitting(true)
    try {
      const path =
        isEdit && existingSigner?.keySlot !== undefined
          ? '/api/org/smart_accounts_setup/replace_email_signer_by_keyslot'
          : '/api/org/smart_accounts_setup/create_email_signer'
      const resp = await serverPostRequest<{
        setup: SmartAccountSetupResponse
      }>({
        path,
        params: {
          orgId: org.id,
          email: data.email,
          name: trimmedName || undefined,
          ...(isEdit && existingSigner?.keySlot !== undefined
            ? { keySlot: existingSigner.keySlot }
            : {}),
        },
        errorMessage: isEdit
          ? 'Failed to update recovery signer'
          : 'Failed to add recovery signer',
      })
      if (resp) {
        writeSetupToCache(resp.data.setup)
        if (!isEdit) {
          await onSignerAdded(resp.data.setup?.eoaSigners?.length ?? 0)
        }
        if (isEdit && existingSigner) {
          await invalidateContactLookup(existingSigner.address)
        }
        onSuccess()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitWallet = async (data: SubmitData, trimmedName: string) => {
    if (!org || !isAddress(data.address)) return
    const formattedAddress = EvmAddress.format(data.address)

    if (
      isEdit &&
      !wasEmail &&
      Address.equal(existingSigner?.address, data.address)
    ) {
      await updateNameOnly(trimmedName)
      return
    }

    setIsSubmitting(true)
    try {
      // Pass keySlot when editing so the backend replaces in place; omit it
      // when adding so the backend assigns the next available slot.
      const resp = await serverPostRequest<{
        setup: SmartAccountSetupResponse
      }>({
        path: '/api/org/smart_accounts_setup/set_eoa_signer_by_keyslot',
        params: {
          orgId: org.id,
          address: formattedAddress,
          name: trimmedName || undefined,
          keySlot:
            isEdit && existingSigner?.keySlot !== undefined
              ? existingSigner.keySlot
              : undefined,
        },
        errorMessage: isEdit
          ? 'Failed to update recovery signer'
          : 'Failed to add recovery signer',
      })
      if (resp) {
        writeSetupToCache(resp.data.setup)
        if (!isEdit) {
          await onSignerAdded(resp.data.setup?.eoaSigners?.length ?? 0)
        }
        if (isEdit && existingSigner) {
          await invalidateContactLookup(existingSigner.address)
        }
        if (trimmedName) await invalidateContactLookup(formattedAddress)
        onSuccess()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSubmit = async (activeTab: 'wallet' | 'email', data: SubmitData) => {
    const trimmedName = data.name.trim()
    if (activeTab === 'email') {
      await submitEmail(data, trimmedName)
    } else {
      await submitWallet(data, trimmedName)
    }
  }

  return { onSubmit, isSubmitting }
}
